'use client'

import { CustomerStory } from '@interfaces/brandHub'
import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { EVENTS } from '@configs/events.config'
import { TENANT_TYPE } from '@components/Ackodrive/common/constants'
import { useAnimateOnVisible } from '@hooks/useAnimateOnVisible'
import CustomerStoryCard from '@components/CustomerStory/CustomerStoryCard/CustomerStoryCard'
import {
  CustomerStoriesListHeadingMock,
  seeMoreCta,
  seeMoreLoadingCta,
} from '@components/CustomerStory/CustomerStoryMock'
import { getMoreStories } from './getMoreStories'
import styles from './CustomerStories.module.scss'

const CustomerStories = () => {
  const [isPending, startTransition] = useTransition()
  const customerStoriesRef = useRef<HTMLDivElement>(null)
  const [windowWidth, setWindowWidth] = useState<number | null>(null)
  const [stories, setStories] = useState<CustomerStory[]>([])

  const curPage = useRef(1)
  const [maxPage, setMaxPage] = useState(1)

  const { ref: titleRef, animationClassName: titleClass } = useAnimateOnVisible<HTMLParagraphElement>()
  const { ref: descRef, animationClassName: descClass } = useAnimateOnVisible<HTMLParagraphElement>({
    animationDelayMs: 60,
  })

  const columnCount = useMemo(() => {
    if (!windowWidth) return 3
    if (windowWidth <= 760) return 1
    if (windowWidth <= 1024) return 2
    return 3
  }, [windowWidth])

  const loadStories = async (firstTime = false) => {
    if (curPage.current > maxPage) return
    startTransition(async () => {
      const data = await getMoreStories(curPage.current)
      if (firstTime) setMaxPage(data.maxPage)
      setStories(prev => [...prev, ...data.stories])
      curPage.current += 1
    })
    if (!firstTime) {
      await trackEvent(EVENTS[TENANT_TYPE.REVIEWS_TESTIMONIAL].engage.eventname, {
        ...EVENTS[TENANT_TYPE.REVIEWS_TESTIMONIAL].engage.eventProps,
        journey: 'more_stories',
      })
    }
  }

  const columns = useMemo(() => {
    const cols: CustomerStory[][] = Array.from({ length: columnCount }, () => [])
    stories.forEach((story, index) => {
      cols[index % columnCount].push(story)
    })
    return cols
  }, [stories, columnCount])

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)

    void loadStories(true)
    handleResize()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div className={styles.customerStoriesContainer}>
      <div className={styles.customerStoriesHeadingContainer}>
        <p ref={titleRef} className={`${styles.customerStoriesHeading} ${titleClass}`}>
          {CustomerStoriesListHeadingMock.title}
        </p>
        <p ref={descRef} className={`${styles.customerStoriesDescription} ${descClass}`}>
          {CustomerStoriesListHeadingMock.description}
        </p>
      </div>
      <div ref={customerStoriesRef} className={styles.customerStories}>
        {columns.map((column, colIndex) => (
          <div key={colIndex} className={styles.customerStoriesList}>
            {colIndex % 2 === 1 && <div style={{ display: 'none' }} />}
            {column.map((story, i) => {
              if (story) return <CustomerStoryCard story={story} key={i} />
              return null
            })}
          </div>
        ))}
      </div>
      {curPage.current !== maxPage && (
        <button
          type="button"
          className={styles.moreReviews}
          onClick={() => {
            void loadStories()
          }}>
          {isPending ? seeMoreLoadingCta : seeMoreCta}
        </button>
      )}
    </div>
  )
}

export default CustomerStories
